import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import { useAccessReview, acknowledgeAccessReview } from "./api";

// fmtDate renders an ISO timestamp as a short local date-time. Returns "—" for empty input.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// fmtExpiry renders a platform-holder's expiresAt as a short "Expires 12 Aug"
// label (Sub-project E4, Task 4/6) — null for a permanent grant (no
// expiresAt) or an unparseable timestamp, so the caller falls back to a
// "Permanent" badge. Mirrors PlatformAssignmentsModal's expiryLabel.
const fmtExpiry = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `Expires ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
};

// AccessReviewPage — the Settings-shell panel for the periodic tenant-owner
// access review (Sub-project D2): shows whether a review is due, when it was
// last acknowledged (and by whom), the current tenant-owner membership, and
// lets a holder of access.review acknowledge the review (resetting the due
// clock). The route itself is capability-gated, so this page only ever
// renders for holders — the inline `canAcknowledge` check further hides the
// write affordance if capabilities were to change underneath an open tab.
export default function AccessReviewPage() {
  const canAcknowledge = useHasCapability(CAPS.ACCESS_REVIEW);
  const queryClient = useQueryClient();
  const reviewQ = useAccessReview();
  const review = reviewQ.data;

  const [notes, setNotes] = useState("");

  const acknowledgeMutation = useMutation(
    () => acknowledgeAccessReview({ notes: notes.trim() || undefined }),
    {
      onSuccess: () => {
        setNotes("");
        toast.success("Access review acknowledged");
        queryClient.invalidateQueries(["access-review"]);
      },
      onError: (err) => {
        toast.error(err?.serverBody?.error?.message || err?.message || "Failed to acknowledge the review. Please try again.");
      },
    }
  );

  const isDue = review?.status === "due";
  const owners = review?.tenantOwners || [];
  const platformHolders = review?.platformHolders || [];
  const platformHolderCount = review?.platformHolderCount ?? platformHolders.length;
  const isLoading = reviewQ.isLoading;
  const error = reviewQ.error;
  const noun = owners.length === 1 ? "tenant owner" : "tenant owners";
  const platformNoun = platformHolderCount === 1 ? "platform assignment" : "platform assignments";

  const columns = [
    {
      accessor: "email",
      Header: () => <span>Email</span>,
      Cell: ({ value }) => <span className="text-text font-medium truncate">{value}</span>,
      flex: 1,
      minWidth: 200,
    },
  ];

  // Platform access panel (Sub-project E4, Task 4): every platform-role
  // (tenant_owner/security_admin) holder tenant-wide, permanent or temporary
  // with its expiry and the reason it was granted — the audit-facing
  // companion to the Manage-assignments dialog (RolesCatalogPage's Platform
  // tab), where the grant/revoke actions actually live. This panel is
  // read-only; it feeds the same acknowledge flow above (no separate ack).
  const platformColumns = [
    {
      accessor: "email",
      Header: () => <span>Email</span>,
      Cell: ({ value }) => <span className="text-text font-medium truncate">{value}</span>,
      flex: 1,
      minWidth: 200,
    },
    {
      accessor: "roleCode",
      Header: () => <span>Role</span>,
      Cell: ({ value }) => <span className="text-text-2">{value}</span>,
      width: 160,
      minWidth: 120,
    },
    {
      id: "duration",
      Header: () => <span>Duration</span>,
      Cell: ({ row }) => {
        const expiry = fmtExpiry(row.original.expiresAt);
        return <Badge status={expiry ? "warning" : "neutral"} label={expiry || "Permanent"} size="sm" />;
      },
      width: 160,
      minWidth: 130,
    },
    {
      accessor: "grantReason",
      Header: () => <span>Reason</span>,
      Cell: ({ value }) => <span className="text-text-2 truncate">{value || "—"}</span>,
      flex: 1,
      minWidth: 160,
    },
  ];

  return (
    <Fragment>
      <AdministrationHeader title="Access review" showPeriodSelector={false} />
      <div className="w-full p-8 space-y-6">
        <p className="text-sm text-text-2 max-w-2xl">
          Periodically confirm who holds tenant-owner access — the platform's most powerful role.
          Acknowledging a review resets the due clock; it does not itself change any assignment.
        </p>

        <div className="rounded-card border border-card-bd bg-card p-5">
          {isLoading ? (
            <div className="flex min-h-[80px] items-center justify-center">
              <div className="h-[24px] w-[24px] animate-spin rounded-full border-b-2 border-text-2" />
            </div>
          ) : error ? (
            <div className="text-danger text-sm">Error loading access review status</div>
          ) : (
            <Fragment>
              <div className="flex items-center gap-3 flex-wrap">
                {isDue ? (
                  <Badge label="Review due" status="warning" />
                ) : (
                  <Badge label="Up to date" status="success" />
                )}
                {typeof review?.daysUntilDue === "number" && (
                  <span className="text-sm text-text-2">
                    {review.daysUntilDue <= 0
                      ? "Due now"
                      : `${review.daysUntilDue} day${review.daysUntilDue === 1 ? "" : "s"} until due`}
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-text-3 text-xs uppercase tracking-wide mb-0.5">Last reviewed</div>
                  <div className="text-text">{fmtDate(review?.lastReviewedAt)}</div>
                  {review?.lastReviewedByUserId != null && (
                    <div className="text-text-3 text-xs mt-0.5">by user #{review.lastReviewedByUserId}</div>
                  )}
                </div>
                <div>
                  <div className="text-text-3 text-xs uppercase tracking-wide mb-0.5">Next due</div>
                  <div className="text-text">{fmtDate(review?.nextDueAt)}</div>
                </div>
                <div>
                  <div className="text-text-3 text-xs uppercase tracking-wide mb-0.5">Review interval</div>
                  <div className="text-text">{review?.intervalDays ?? "—"} days</div>
                </div>
                <div>
                  <div className="text-text-3 text-xs uppercase tracking-wide mb-0.5">Platform assignments</div>
                  <div className="text-text">
                    {platformHolderCount} {platformNoun}
                  </div>
                </div>
              </div>

              {canAcknowledge && (
                <div className="mt-5 pt-4 border-t border-card-bd">
                  <label htmlFor="access-review-notes" className="block mb-1 text-sm font-medium text-text">
                    Notes (optional)
                  </label>
                  <input
                    id="access-review-notes"
                    type="text"
                    value={notes}
                    maxLength={500}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. confirmed both tenant owners still need access"
                    className="border border-solid border-border rounded-ctrl px-3 py-1.5 text-sm w-full box-border focus:outline-none focus:ring-1 focus:ring-primary mb-3"
                    style={{ maxWidth: "480px" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!acknowledgeMutation.isLoading) acknowledgeMutation.mutate();
                    }}
                    disabled={acknowledgeMutation.isLoading}
                    className="px-3 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors border-0 box-border disabled:opacity-50"
                  >
                    {acknowledgeMutation.isLoading ? "Acknowledging…" : "Acknowledge review"}
                  </button>
                </div>
              )}
            </Fragment>
          )}
        </div>

        <TableCard
          title="Tenant owner members"
          subtitle={isLoading ? "Loading…" : `Showing ${owners.length} ${noun}`}
        >
          <p className="text-xs text-text-3 mb-3 -mt-1">
            Keep this to 1–2 people — tenant owner grants the platform's full authority and should
            not be widely held.
          </p>
          {owners.length === 0 ? (
            <EmptyState
              icon={icon({ name: "user-shield", style: "light" })}
              title="No tenant owners found"
              message="This tenant currently has no user assigned the tenant-owner role."
            />
          ) : (
            <FlexTable columns={columns} data={owners} getRowId={(o) => o.id} />
          )}
        </TableCard>

        <TableCard
          title="Platform access"
          subtitle={isLoading ? "Loading…" : `${platformHolderCount} ${platformNoun} across every platform role`}
        >
          <p className="text-xs text-text-3 mb-3 -mt-1">
            Every user currently holding a platform role (tenant owner or security administrator),
            including time-boxed temporary elevations and when each expires. Grant or revoke access
            from the Platform tab of Roles &amp; Access.
          </p>
          {platformHolders.length === 0 ? (
            <EmptyState
              icon={icon({ name: "user-shield", style: "light" })}
              title="No platform assignments found"
              message="No user currently holds a platform role in this tenant."
            />
          ) : (
            <FlexTable
              columns={platformColumns}
              data={platformHolders}
              getRowId={(h) => `${h.userId}-${h.roleCode}`}
            />
          )}
          <div className="mt-3 pt-3 border-t border-border">
            <Link
              to="/settings/roles?tab=platform"
              className="text-xs font-medium text-primary hover:text-primary-hover"
            >
              Review now — open Platform tab
            </Link>
          </div>
        </TableCard>
      </div>
    </Fragment>
  );
}
